import React, { useCallback, useState } from 'react';
import Checkbox from './FormCheckbox';
import { Option } from 'model/Form';
import FormElemWrapper from './FormElemWrapper';

const CheckboxGroup: React.FC<{
  optionList: Option[];
  instructions: string;
  required: boolean;
  onChange: (selected: boolean, option: Option) => void;
  onError: (err: boolean) => void;
  onUpdateDataLayer: () => void;
}> = ({ optionList, instructions, required, onChange, onError, onUpdateDataLayer }) => {
  const checkboxes = optionList.map((option, i) => (
    <Checkbox
      key={i}
      required={required}
      onChange={(selected) => onChange(selected, option)}
      onUpdateDataLayer={onUpdateDataLayer}
      onError={onError}
      slots={{
        label: (
          <div slot="label" className="body-small">
            {option.displayName}
          </div>
        ),
        instructions: instructions && <div slot="instructions">{instructions}</div>,
      }}
    ></Checkbox>
  ));
  return <div className="d-flex flex-column gap-3">{checkboxes}</div>;
};

type MultipleCheckboxesProps = {
  optionList: Option[];
  label: string;
  required?: boolean;
  instructions?: string;
  requiredErrorMessage?: string;
  onChange: (newOptions: string[]) => void;
  onUpdateDataLayer: () => void;
};

const MultipleCheckboxes: React.FC<MultipleCheckboxesProps> = ({
  optionList,
  label,
  required = false,
  instructions = '',
  requiredErrorMessage,
  onUpdateDataLayer,
  onChange,
}) => {
  const [isError, setIsError] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);

  const showRequired = required && selectedOptions.length === 0;
  const handleSetError = (value: boolean) => {
    setIsError(value);
  };

  const onSelect = useCallback(
    (selected: boolean, option: Option) => {
      const newOptions = selected
        ? [...selectedOptions, option]
        : selectedOptions.filter((o) => o !== option);
      onChange(newOptions.map((o) => o.value));
      setSelectedOptions(newOptions);
    },
    [onChange, selectedOptions]
  );

  return (
    <FormElemWrapper
      wrapperTag="div"
      isError={isError}
      slots={{
        fieldLabel: label,
        error: (
          <span className="form-element__hints form-element__error field-error-text text-error-400">
            {requiredErrorMessage}
          </span>
        ),
      }}
    >
      <CheckboxGroup
        optionList={optionList}
        instructions={instructions}
        required={showRequired}
        onChange={onSelect}
        onError={handleSetError}
        onUpdateDataLayer={onUpdateDataLayer}
      />
    </FormElemWrapper>
  );
};

export default MultipleCheckboxes;
